import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, Download, Filter } from 'lucide-react';
import { PriorityBadge, SourceBadge, StageBadge, DealTypeBadge } from './Badges';
import { formatAED, initials, formatDateTime } from '@/lib/format';
import { STAGES, SOURCES, PRIORITIES } from '@/lib/constants';

export default function ListView({ contacts, agents = [] }) {
    const navigate = useNavigate();
    const [q, setQ] = useState('');
    const [stage, setStage] = useState('');
    const [source, setSource] = useState('');
    const [priority, setPriority] = useState('');
    const [page, setPage] = useState(1);
    const PAGE_SIZE = 25;

    const filtered = useMemo(() => {
        const ql = q.toLowerCase();
        return contacts.filter((c) => {
            if (stage && c.pipeline_stage !== stage) return false;
            if (source && c.source !== source) return false;
            if (priority && c.priority !== priority) return false;
            if (ql && !`${c.full_name} ${c.phone} ${c.email}`.toLowerCase().includes(ql)) return false;
            return true;
        });
    }, [contacts, q, stage, source, priority]);

    const pages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
    const pageRows = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

    const exportCSV = () => {
        const headers = ['Full Name', 'Phone', 'Email', 'Stage', 'Priority', 'Source', 'Budget AED', 'Property', 'Emirate', 'Agent', 'Created'];
        const rows = filtered.map((c) => [
            c.full_name, c.phone, c.email, c.pipeline_stage, c.priority, c.source === 'Other' && c.source_detail ? `Other - ${c.source_detail}` : c.source, c.budget_aed, c.property_type, c.emirate_preference,
            agents.find((a) => a.id === c.assigned_to)?.full_name || '',
            c.created_at,
        ]);
        const csv = [headers, ...rows].map((r) => r.map((v) => `"${(v ?? '').toString().replace(/"/g, '""')}"`).join(',')).join('\n');
        const blob = new Blob([csv], { type: 'text/csv' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a'); a.href = url; a.download = `pipeline-${new Date().toISOString().slice(0,10)}.csv`; a.click(); URL.revokeObjectURL(url);
    };

    return (
        <div className="px-4 lg:px-6 pb-6">
            <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
                <div className="p-4 border-b border-gray-100 flex flex-wrap items-center gap-3">
                    <div className="relative flex-1 min-w-[220px]">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                        <input value={q} onChange={(e) => { setQ(e.target.value); setPage(1); }} placeholder="Search by name, phone, email…" className="w-full h-10 pl-9 pr-3 rounded-md border border-gray-300 text-[14px] outline-none focus:border-aks-gold aks-focus-ring" data-testid="list-search" />
                    </div>
                    <select value={stage} onChange={(e) => { setStage(e.target.value); setPage(1); }} className="h-10 px-3 rounded-md border border-gray-300 text-[13px]"><option value="">All stages</option>{STAGES.map((s) => <option key={s} value={s}>{s}</option>)}</select>
                    <select value={source} onChange={(e) => { setSource(e.target.value); setPage(1); }} className="h-10 px-3 rounded-md border border-gray-300 text-[13px]"><option value="">All sources</option>{SOURCES.map((s) => <option key={s} value={s}>{s}</option>)}</select>
                    <select value={priority} onChange={(e) => { setPriority(e.target.value); setPage(1); }} className="h-10 px-3 rounded-md border border-gray-300 text-[13px]"><option value="">All priorities</option>{PRIORITIES.map((p) => <option key={p} value={p}>{p}</option>)}</select>
                    <button onClick={exportCSV} className="h-10 px-3 inline-flex items-center gap-2 rounded-md border border-gray-300 hover:border-aks-gold text-[13px] font-medium" data-testid="export-csv-btn">
                        <Download className="h-4 w-4" /> Export CSV
                    </button>
                </div>
                <div className="overflow-x-auto">
                    <table className="w-full text-[13px]">
                        <thead className="bg-gray-50 text-gray-600 text-[11px] uppercase tracking-wider">
                            <tr>
                                <th className="text-left p-3">Name</th>
                                <th className="text-left p-3">Stage</th>
                                <th className="text-left p-3">Priority</th>
                                <th className="text-left p-3">Source</th>
                                <th className="text-left p-3">Deal Type</th>
                                <th className="text-left p-3">Budget</th>
                                <th className="text-left p-3">Property</th>
                                <th className="text-left p-3">Agent</th>
                                <th className="text-left p-3">Next follow-up</th>
                            </tr>
                        </thead>
                        <tbody>
                            {pageRows.length === 0 ? (
                                <tr><td colSpan={9} className="text-center p-10 text-gray-400 text-[13px]">No contacts match your filters</td></tr>
                            ) : pageRows.map((c) => {
                                const agent = agents.find((a) => a.id === c.assigned_to);
                                return (
                                    <tr key={c.id} className="border-t border-gray-100 hover:bg-gray-50 cursor-pointer" onClick={() => navigate(`/pipeline/${c.id}`)} data-testid={`list-row-${c.id}`}>
                                        <td className="p-3">
                                            <div className="font-semibold text-aks-text">{c.full_name}</div>
                                            <div className="text-[11px] text-gray-500">{c.phone || c.email}</div>
                                        </td>
                                        <td className="p-3"><StageBadge value={c.pipeline_stage} /></td>
                                        <td className="p-3"><PriorityBadge value={c.priority} /></td>
                                        <td className="p-3"><SourceBadge value={c.source} detail={c.source_detail} /></td>
                                        <td className="p-3"><DealTypeBadge value={c.deal_type} /></td>
                                        <td className="p-3">{formatAED(c.budget_aed)}</td>
                                        <td className="p-3 capitalize">{c.property_type || '—'}<div className="text-[10px] text-gray-500">{c.emirate_preference || ''}</div></td>
                                        <td className="p-3">
                                            {agent ? (
                                                <div className="flex items-center gap-2">
                                                    <div className="h-6 w-6 rounded-full bg-aks-navy text-white flex items-center justify-center text-[10px] font-semibold">{initials(agent.full_name)}</div>
                                                    <span className="text-[12px]">{agent.full_name}</span>
                                                </div>
                                            ) : <span className="text-gray-400">Unassigned</span>}
                                        </td>
                                        <td className="p-3 text-[12px]">{formatDateTime(c.next_followup_at)}</td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
                <div className="p-3 border-t border-gray-100 flex items-center justify-between">
                    <div className="text-[12px] text-gray-500">Showing {pageRows.length} of {filtered.length}</div>
                    <div className="flex items-center gap-2">
                        <button onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page === 1} className="h-8 px-3 rounded border border-gray-300 text-[12px] disabled:opacity-50">Prev</button>
                        <span className="text-[12px]">Page {page} / {pages}</span>
                        <button onClick={() => setPage((p) => Math.min(pages, p + 1))} disabled={page >= pages} className="h-8 px-3 rounded border border-gray-300 text-[12px] disabled:opacity-50">Next</button>
                    </div>
                </div>
            </div>
        </div>
    );
}
